import { useState } from "react";
import { Link } from "react-router-dom";
import { FaExternalLinkAlt, FaHeart, FaCheckCircle, FaMapMarkerAlt, FaBriefcase } from "react-icons/fa";
import { likeJob, unlikeJob, applyToJob } from "../../apis/JobApis";
import { useTheme } from "../Theme/ThemeContext";

export function JobCard({ job, user = {} }) {
  const { userId, role } = user;
  const { currentTheme } = useTheme();
  const initiallyLiked = job.likes && job.likes[userId];
  const [liked, setLiked] = useState(Boolean(initiallyLiked));
  const [likeCount, setLikeCount] = useState(
    job.likes ? Object.keys(job.likes).length : 0
  );
  const [appliesCount, setAppliesCount] = useState(
    job.applies ? Object.keys(job.applies).length : 0
  );

  const locations = Array.isArray(job?.locations)
    ? job.locations
    : job?.location
      ? [job.location]
      : [];
  const experiences = Array.isArray(job?.experience)
    ? job.experience
    : job?.experienceLevel
      ? [job.experienceLevel]
      : [];

  const companyName = job?.companyName || "";
  const title = job?.title || "Job";
  const companyInitial = String(companyName || "C").trim().slice(0, 1).toUpperCase();
  const logoUrl = job?.companyLogo || "";

  const handleLike = async () => {
    try {
      if (liked) {
        await unlikeJob(job.id, userId);
        setLikeCount((prev) => prev - 1);
      } else {
        await likeJob(job.id, userId);
        setLikeCount((prev) => prev + 1);
      }
      setLiked(!liked);
    } catch (err) {
      console.error("Error liking/unliking job:", err);
    }
  };

  const handleApply = async () => {
    try {
      await applyToJob(job.id, userId);
      setAppliesCount((prev) => prev + 1);
      window.open(job.applyUrl, "_blank");
    } catch (err) {
      console.error("Error applying to job:", err);
    }
  };

  // Get type badge color
  const getTypeBadge = () => {
    const type = String(job?.type || "").toLowerCase();
    if (type === "internship") return "bg-royal-50 dark:bg-royal-900/30 text-royal-700 dark:text-royal-300 border-royal-200 dark:border-royal-800";
    if (type === "fulltime" || type === "full-time") return "bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800";
    return "bg-accent-50 dark:bg-accent-900/30 text-accent-700 dark:text-accent-300 border-accent-200 dark:border-accent-800";
  };

  return (
    <div className="ca-card-hover p-0 overflow-hidden group">
      <div className="p-5 sm:p-6 flex flex-col gap-4">
        {/* Header */}
        <div className="flex items-start gap-4">
          {/* Logo */}
          <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-xl overflow-hidden border border-neutral-200/50 dark:border-white/10 bg-white dark:bg-neutral-800 flex items-center justify-center flex-shrink-0 shadow-sm">
            {logoUrl ? (
              <img
                src={logoUrl}
                alt={`${companyName || "Company"} logo`}
                className="w-full h-full object-contain p-1"
                loading="lazy"
              />
            ) : (
              <span className="text-lg font-bold text-neutral-400 dark:text-neutral-500">{companyInitial}</span>
            )}
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-2">
              <div>
                <h3 className="text-lg font-bold text-neutral-800 dark:text-white leading-snug line-clamp-2 group-hover:text-accent-600 dark:group-hover:text-accent-400 transition-colors">
                  {title}
                </h3>
                <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-1 flex items-center gap-1.5">
                  <FaBriefcase className="w-3 h-3 text-neutral-400 dark:text-neutral-500" />
                  {companyName}
                </p>
              </div>
              
              {/* Like Button */}
              <button
                type="button"
                onClick={handleLike}
                className={`p-2 rounded-full transition-all ${
                  liked 
                    ? "bg-red-50 dark:bg-red-900/30 text-red-500" 
                    : "bg-neutral-100/50 dark:bg-white/5 text-neutral-400 dark:text-neutral-500 hover:bg-neutral-200/50 dark:hover:bg-white/10"
                }`}
                title={liked ? "Unlike" : "Like"}
              >
                <FaHeart className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Tags */}
        <div className="flex flex-wrap items-center gap-2">
          {job?.isVerified && (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold bg-accent-50 dark:bg-accent-900/30 text-accent-700 dark:text-accent-300 border border-accent-100 dark:border-accent-800">
              <FaCheckCircle className="w-3 h-3" /> Verified
            </span>
          )}

          {job?.type && (
            <span className={`px-2.5 py-1 rounded-lg text-xs font-semibold border capitalize ${getTypeBadge()}`}>
              {job.type}
            </span>
          )}

          {locations.slice(0, 2).map((loc, i) => (
            <span
              key={`${loc}-${i}`}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium bg-neutral-100/50 dark:bg-white/5 text-neutral-600 dark:text-neutral-300"
            >
              <FaMapMarkerAlt className="w-2.5 h-2.5" /> {loc}
            </span>
          ))}

          {experiences.slice(0, 1).map((exp, i) => (
            <span
              key={`${exp}-${i}`}
              className="px-2.5 py-1 rounded-lg text-xs font-medium bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 border border-amber-100 dark:border-amber-800"
            >
              {exp}
            </span>
          ))}
        </div>

        {/* Meta row */}
        <div className="flex items-center justify-between gap-3 pt-2 border-t border-neutral-200/30 dark:border-white/10">
          <div className="flex items-center gap-3 text-xs text-neutral-500 dark:text-neutral-400">
            <span className="flex items-center gap-1">
              <FaHeart className={liked ? "text-red-500" : "text-neutral-400 dark:text-neutral-500"} /> {likeCount}
            </span>
            {role === "admin" && (
              <span>{appliesCount} applies</span>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3 pt-1">
          <Link 
            to={`/jobs/${job.id}`} 
            className="flex-1 text-center py-2.5 px-4 rounded-xl text-sm font-semibold text-neutral-700 dark:text-neutral-300 bg-neutral-100/50 dark:bg-white/5 hover:bg-neutral-200/50 dark:hover:bg-white/10 transition-all duration-200"
          >
            View Details
          </Link>

          <button 
            onClick={handleApply} 
            className={`flex-1 inline-flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl text-sm font-semibold text-white bg-gradient-to-r ${currentTheme.colors.gradient} hover:opacity-90 transition-all shadow-sm ${currentTheme.colors.glow} hover:shadow-md`}
          >
            Apply <FaExternalLinkAlt className="w-3 h-3" />
          </button>
        </div>
      </div>
    </div>
  );
}
